import React, { useCallback, useEffect, useState } from 'react';
import { Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Spinner } from '../../design-system';
import toast from '../../design-system/toast';
import {
  ACCESS_KEY_ID_PATTERN,
  CODECOMMIT_REGIONS,
  CodeCommitAuthMode,
  CodeCommitConnectPayload,
  CodeCommitRoleSetup,
  DEFAULT_CODECOMMIT_REGION,
  ROLE_ARN_PATTERN,
  codecommitTrustPolicy,
} from '../../types/codecommit';

export interface CodeCommitConnectFormProps {
  /** Fetches the principal ARN + external ID the customer's role must trust (IAM role mode). */
  loadRoleSetup: () => Promise<CodeCommitRoleSetup>;
  /** Performs the connect request. Throwing surfaces the error inline. */
  onSubmit: (payload: CodeCommitConnectPayload) => Promise<void>;
  onCancel?: () => void;
  readOnly?: boolean;
  submitLabel?: string;
  /** Full-width layout (onboarding) vs. the compact settings column. */
  layout?: 'compact' | 'wide';
}

const INPUT_CLS =
  'w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm';
const LABEL_CLS = 'text-xs font-medium text-ui-text-secondary';
const READONLY_CLS =
  'flex-1 min-w-0 px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-xs font-mono truncate';
const COPY_BTN_CLS =
  'inline-flex items-center gap-1 px-2.5 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-xs hover:bg-ui-background-tertiary transition-colors whitespace-nowrap';

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success('Copied to clipboard');
  } catch {
    toast.error('Failed to copy');
  }
};

/**
 * Credential form for connecting an AWS account's CodeCommit repositories. Owns field
 * state and validation; the parent owns the request (onboarding must first make sure a
 * real organization exists, settings already has one).
 */
const CodeCommitConnectForm: React.FC<CodeCommitConnectFormProps> = ({
  loadRoleSetup,
  onSubmit,
  onCancel,
  readOnly = false,
  submitLabel = 'Connect',
  layout = 'compact',
}) => {
  const [label, setLabel] = useState('');
  const [region, setRegion] = useState(DEFAULT_CODECOMMIT_REGION);
  const [authMode, setAuthMode] = useState<CodeCommitAuthMode>('access_keys');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [roleSetup, setRoleSetup] = useState<CodeCommitRoleSetup | null>(null);
  const [roleSetupLoading, setRoleSetupLoading] = useState(false);
  const [roleSetupError, setRoleSetupError] = useState<string | null>(null);
  // The deployment has no worker role / external-id secret configured (backend answers 400).
  const [roleSetupUnavailable, setRoleSetupUnavailable] = useState(false);
  const [showTrustPolicy, setShowTrustPolicy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchRoleSetup = useCallback(async () => {
    setRoleSetupLoading(true);
    setRoleSetupError(null);
    try {
      const setup = await loadRoleSetup();
      setRoleSetup(setup);
      setRoleSetupUnavailable(false);
    } catch (e: any) {
      console.error('Failed to load CodeCommit role setup', e);
      if (e?.status === 400) {
        setRoleSetupUnavailable(true);
      } else {
        setRoleSetupError("Couldn't load the role trust details. Try again.");
      }
    } finally {
      setRoleSetupLoading(false);
    }
  }, [loadRoleSetup]);

  // Load the trust details the first time the IAM-role mode is chosen.
  useEffect(() => {
    if (authMode === 'iam_role' && !roleSetup && !roleSetupLoading && !roleSetupError && !roleSetupUnavailable) {
      fetchRoleSetup();
    }
  }, [authMode, roleSetup, roleSetupLoading, roleSetupError, roleSetupUnavailable, fetchRoleSetup]);

  const switchMode = (mode: CodeCommitAuthMode) => {
    setAuthMode(mode);
    setFormError(null);
  };

  const accessKeyHint =
    authMode === 'access_keys' && accessKeyId.trim() && !ACCESS_KEY_ID_PATTERN.test(accessKeyId.trim())
      ? 'Access key IDs usually start with AKIA and are 20 characters long.'
      : null;

  const handleSubmit = async () => {
    setFormError(null);
    const trimmedLabel = label.trim();
    if (authMode === 'access_keys') {
      const key = accessKeyId.trim();
      const secret = secretAccessKey.trim();
      if (!key || !secret) {
        setFormError('Access key ID and secret access key are required.');
        return;
      }
      setSubmitting(true);
      try {
        await onSubmit({
          aws_region: region,
          auth_mode: 'access_keys',
          label: trimmedLabel || undefined,
          access_key_id: key,
          secret_access_key: secret,
        });
      } catch (e: any) {
        setFormError(
          e?.data?.detail || e?.message || 'Could not connect to AWS CodeCommit. Check your region and credentials.'
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const arn = roleArn.trim();
    if (!roleSetup) {
      setFormError('Load the role trust details before connecting.');
      return;
    }
    if (!ROLE_ARN_PATTERN.test(arn)) {
      setFormError('Enter a role ARN like arn:aws:iam::123456789012:role/KolegaCodeCommit.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        aws_region: region,
        auth_mode: 'iam_role',
        label: trimmedLabel || undefined,
        role_arn: arn,
      });
    } catch (e: any) {
      setFormError(
        e?.data?.detail || e?.message || 'Could not assume the role. Check the trust policy and role ARN.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const connectDisabled = submitting || readOnly || (authMode === 'iam_role' && !roleSetup);
  const modeBtn = (active: boolean) =>
    `flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
      active
        ? 'bg-brand-primary/20 text-brand-primary dark:text-brand-primaryDarkMode border-brand-primary/30'
        : 'bg-ui-background-tertiary text-ui-text-secondary border-transparent hover:bg-ui-background-tertiary'
    }`;

  return (
    <div className={`grid gap-4 ${layout === 'compact' ? 'max-w-md' : ''}`}>
      <div className="rounded-lg bg-ui-background-tertiary px-4 py-3 text-sm text-ui-text-secondary space-y-1.5">
        <p className="font-medium text-ui-text-secondary">How Kolega connects to CodeCommit</p>
        <p>
          Kolega lists repositories, clones them for scanning and opens pull requests using an IAM
          identity you control. Use short-lived role assumption where you can; long-term access keys
          are stored encrypted. One connection covers one AWS account in one region.
        </p>
      </div>

      <div className="grid gap-3">
        {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}

        <div className="grid gap-1">
          <label className={LABEL_CLS} htmlFor="codecommit-label">
            Label <span className="font-normal text-ui-text-tertiary">(optional)</span>
          </label>
          <input
            id="codecommit-label"
            type="text"
            placeholder="e.g. Production account"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className={INPUT_CLS}
          />
        </div>

        <div className="grid gap-1">
          <label className={LABEL_CLS} htmlFor="codecommit-region">
            AWS region
          </label>
          <select
            id="codecommit-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className={INPUT_CLS}
          >
            {CODECOMMIT_REGIONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-ui-text-tertiary">
            One region per connection. Add another connection for other regions.
          </p>
        </div>

        <div className="grid gap-1">
          <span className={LABEL_CLS}>Authentication</span>
          <div role="radiogroup" aria-label="Authentication method" className="flex gap-1.5">
            <button
              type="button"
              role="radio"
              aria-checked={authMode === 'access_keys'}
              onClick={() => switchMode('access_keys')}
              className={modeBtn(authMode === 'access_keys')}
            >
              Access keys
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={authMode === 'iam_role'}
              onClick={() => switchMode('iam_role')}
              className={modeBtn(authMode === 'iam_role')}
            >
              IAM role
            </button>
          </div>
        </div>

        {authMode === 'access_keys' ? (
          <>
            <div className="grid gap-1">
              <label className={LABEL_CLS} htmlFor="codecommit-access-key-id">
                Access key ID
              </label>
              <input
                id="codecommit-access-key-id"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="AKIA…"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                className={`${INPUT_CLS} font-mono`}
              />
              {accessKeyHint && <p className="text-xs text-ui-text-tertiary">{accessKeyHint}</p>}
            </div>
            <div className="grid gap-1">
              <label className={LABEL_CLS} htmlFor="codecommit-secret-access-key">
                Secret access key
              </label>
              <input
                id="codecommit-secret-access-key"
                type="password"
                autoComplete="off"
                placeholder="Paste the secret access key"
                value={secretAccessKey}
                onChange={(e) => setSecretAccessKey(e.target.value)}
                className={INPUT_CLS}
              />
              <p className="text-xs text-ui-text-tertiary">
                Attach the AWSCodeCommitPowerUser policy (or equivalent) to the IAM user so Kolega can
                read repositories, push fix branches and open pull requests.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-ui-border-primary bg-ui-background-tertiary p-3 grid gap-3">
              <p className="text-xs font-medium text-ui-text-secondary">Trust this principal</p>
              {roleSetupLoading ? (
                <div className="flex items-center gap-2 text-xs text-ui-text-tertiary">
                  <Spinner size={14} /> Loading trust details…
                </div>
              ) : roleSetupUnavailable ? (
                <p className="text-xs text-ui-text-secondary">
                  IAM role connections are not available on this deployment yet. Use access keys instead.
                </p>
              ) : roleSetupError ? (
                <div className="grid gap-2">
                  <p className="text-xs text-red-600 dark:text-red-400">{roleSetupError}</p>
                  <button type="button" onClick={fetchRoleSetup} className={`${COPY_BTN_CLS} justify-self-start`}>
                    Try again
                  </button>
                </div>
              ) : roleSetup ? (
                <>
                  <div className="grid gap-1">
                    <span className="text-xs text-ui-text-tertiary">Principal ARN</span>
                    <div className="flex gap-2">
                      <input readOnly value={roleSetup.kolega_principal_arn} aria-label="Principal ARN" className={READONLY_CLS} />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(roleSetup.kolega_principal_arn)}
                        className={COPY_BTN_CLS}
                        aria-label="Copy principal ARN"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-1">
                    <span className="text-xs text-ui-text-tertiary">External ID</span>
                    <div className="flex gap-2">
                      <input readOnly value={roleSetup.external_id} aria-label="External ID" className={READONLY_CLS} />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(roleSetup.external_id)}
                        className={COPY_BTN_CLS}
                        aria-label="Copy external ID"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-1">
                    <button
                      type="button"
                      onClick={() => setShowTrustPolicy((v) => !v)}
                      className="inline-flex items-center gap-1 text-xs text-ui-text-secondary hover:text-ui-text-primary transition-colors justify-self-start"
                      aria-expanded={showTrustPolicy}
                    >
                      {showTrustPolicy ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      Trust policy JSON
                    </button>
                    {showTrustPolicy && (
                      <div className="grid gap-2">
                        <pre className="text-[11px] font-mono whitespace-pre overflow-x-auto rounded border border-ui-border-primary bg-ui-background-secondary p-2 text-ui-text-primary">
                          {codecommitTrustPolicy(roleSetup)}
                        </pre>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(codecommitTrustPolicy(roleSetup))}
                          className={`${COPY_BTN_CLS} justify-self-start`}
                        >
                          <Copy size={12} /> Copy trust policy
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>

            <div className="rounded-lg bg-ui-background-tertiary px-4 py-3 text-sm text-ui-text-secondary space-y-1.5">
              <p className="font-medium text-ui-text-secondary">How to create the role</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>In the AWS console open <strong className="font-medium">IAM → Roles → Create role</strong></li>
                <li>Choose <strong className="font-medium">Custom trust policy</strong> and paste the trust policy above</li>
                <li>Attach <strong className="font-medium">AWSCodeCommitPowerUser</strong> (needed to push fix branches and open pull requests)</li>
                <li>Create the role and copy its <strong className="font-medium">Role ARN</strong> below</li>
              </ol>
            </div>

            <div className="grid gap-1">
              <label className={LABEL_CLS} htmlFor="codecommit-role-arn">
                Role ARN
              </label>
              <input
                id="codecommit-role-arn"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="arn:aws:iam::123456789012:role/KolegaCodeCommit"
                value={roleArn}
                onChange={(e) => setRoleArn(e.target.value)}
                className={`${INPUT_CLS} font-mono`}
              />
            </div>
          </>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={connectDisabled}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
          >
            {submitting ? (
              <>
                <Spinner size={14} tone="current" />
                Connecting…
              </>
            ) : (
              submitLabel
            )}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 rounded-lg transition-colors text-sm bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeCommitConnectForm;
